import React, { useState, useEffect } from "react";
import {
  Card,
  Typography,
  Form,
  Input,
  Button,
  Alert,
  message,
  Spin,
} from "antd";
import {
  BankOutlined,
  EditOutlined,
} from "@ant-design/icons";
import { useAuth0 } from "@auth0/auth0-react";
import { useOrganization } from "../../contexts/organization-context";
import apiClient from "../../utils/axios";

const { Title } = Typography;

interface OrganizationData {
  organization_id: string;
  organization_name: string;
}

export const Organization: React.FC = () => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [organizationData, setOrganizationData] = useState<OrganizationData | null>(null);
  const { getAccessTokenSilently } = useAuth0();
  const { ownedOrganizations, isOrganizationOwner, refreshOrganizations } = useOrganization();

  const fetchOrganizationData = async () => {
    if (!isOrganizationOwner || ownedOrganizations.length === 0) return;

    try {
      setLoading(true);
      
      // Use organization data from context to avoid duplicate API calls
      const ownedOrg = ownedOrganizations[0];
      const data = {
        organization_id: ownedOrg.id,
        organization_name: ownedOrg.name,
      };
      
      setOrganizationData(data);
      form.setFieldsValue({
        organization_name: data.organization_name,
      });
      
    } catch (error) {
      console.error("Error setting organization data:", error);
      message.error("Failed to load organization data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrganizationData();
  }, [isOrganizationOwner, ownedOrganizations]);

  const handleSubmit = async (values: any) => {
    if (!organizationData) return;

    try {
      setSaving(true);
      const token = await getAccessTokenSilently();
      
      // Update organization name if it's changed
      if (values.organization_name && values.organization_name !== organizationData.organization_name) {
        await apiClient.put(`/api/organization/${organizationData.organization_id}`, {
          name: values.organization_name,
        }, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });
      }
      
      message.success("Organization updated successfully");
      await fetchOrganizationData();
      await refreshOrganizations();
    } catch (error: any) {
      console.error("Error updating organization:", error);
      message.error(error.response?.data?.detail || "Failed to update organization");
    } finally {
      setSaving(false);
    }
  };

  if (!isOrganizationOwner) {
    return (
      <Card>
        <Alert
          message="Access Denied"
          description="Only organization owners can manage organization settings."
          type="error"
          showIcon
        />
      </Card>
    );
  }

  if (loading) {
    return (
      <Card>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
        </div>
      </Card>
    );
  }

  if (!organizationData) {
    return (
      <Card>
        <Alert
          message="No Organization Data"
          description="Unable to load organization information."
          type="warning"
          showIcon
        />
      </Card>
    );
  }

  return (
    <div style={{ padding: '24px' }}>
      <Title level={2}>
        <BankOutlined /> Organization Settings
      </Title>
      
      <Card title="General Settings" style={{ maxWidth: 600 }}>
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          initialValues={{
            organization_name: organizationData.organization_name,
          }}
        >
          <Form.Item
            name="organization_name"
            label="Organization Name"
            rules={[
              { required: true, message: 'Please enter organization name' },
              { min: 2, message: 'Organization name must be at least 2 characters' },
              { max: 100, message: 'Organization name must be at most 100 characters' },
            ]}
          >
            <Input 
              prefix={<EditOutlined />}
              placeholder="Enter organization name"
              size="large"
            />
          </Form.Item>

          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving} size="large">
              Update Organization
            </Button>
          </Form.Item>
        </Form>
      </Card>
    </div>
  );
}; 